import { useTranslation } from "@msuite/ui/localization";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { guardArray } from "@msuite/utils/array";
import { TEAM_FILTER_ALL, TEAM_FILTER_UNASSIGNED } from "@msuite/utils/schemas";
import type { TeamPickerController } from "@/hooks/use-team-picker";

/** Props Interface */
interface TeamPickerProps {
	controller: TeamPickerController;
}

export function TeamPicker({ controller }: TeamPickerProps) {
	/** Context */
	const { t } = useTranslation();

	/** Computed */
	const guardedTeams = guardArray(controller.teams);
	// The two entries that are not teams sit at the ends, divided off from the team names
	// between them: one widens the list to everybody, the other narrows it to the people a
	// picker made only of teams would never show.
	const items = [
		...(controller.withAllOption
			? [{ label: t("general.all"), value: TEAM_FILTER_ALL, cyId: "team-all" }, "divider" as const]
			: []),
		...guardedTeams.map((team, index) => ({
			label: team.name,
			value: team.teamId,
			cyId: `team-${index}`,
		})),
		...(controller.withUnassignedOption
			? [
					"divider" as const,
					{
						label: t("organization.without_team"),
						value: TEAM_FILTER_UNASSIGNED,
						cyId: "team-unassigned",
					},
				]
			: []),
	];

	/** Guard */
	if (!controller.teamId) return null;
	if (guardedTeams.length === 0) return null;

	/** Render */
	return (
		<SegmentedControl
			value={controller.teamId}
			items={items}
			onChange={controller.setTeamId}
		/>
	);
}
